import React, { useState, useRef, useEffect, useCallback } from "react";
import "../styles/ResultsPage.css";
import { FiltersSection } from "./FiltersSection";
import { JobTitles } from "./JobTitles";
import { IndexHeader } from "./IndexHeader";
import { updateColors, useTheme } from "../hooks/ThemeContext";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/TutorialDriver.css";
import { TourButton } from "./TourButton";
import { RegisterEmail } from "./RegisterEmail";
import * as Sentry from '@sentry/react';

function Results() {
  const [user, setUser] = useState({}); //User object
  const [infoMessage, setInfoMessage] = useState(""); //General info/success message
  const [showInfoPopup, setShowInfoPopup] = useState(false); //Show the info popup
  const [jobs, setJobs] = useState({}); //List of jobs
  const [favoriteCompanyJobs, setFavoriteCompanyJobs] = useState([]); //List of jobs
  const [loginRefresh, setLoginRefresh] = useState(false);
  const [total_jobs, setTotalJobs] = useState(0); //Total jobs available for user
  const [distinct_titles, setDistinctTitles] = useState([]); //List of common job titles in jobs list
  const [error_message, setErrorMessage] = useState("");
  const [firstWait, setFirstWait] = useState(false); //if backend has responded (true) or not (false) [FOR JOB TITLES]
  const [userWait, setUserWait] = useState(false); //if backend has responded (true) or not (false) [FOR USER]
  const [refreshVal, setRefreshVal] = useState(0); //used for refreshing/recalling api call via useEffect
  const [premiumPopup, setPremiumPopup] = useState(false); //if premiumn popup is open or nah
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [selectedJobType, setSelectedJobType] = useState("internships");
  const [filtersDisabled, setFiltersDisabled] = useState(false);

  // Company Search & Favorites state
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]); // Changed to array for multi-select
  const [favoriteCompanyIds, setFavoriteCompanyIds] = useState([]);
  const [favoriteCompanyInfo, setFavoriteCompanyInfo] = useState([]);
  const [favoritesSubTab, setFavoritesSubTab] = useState("jobs"); // 'jobs' or 'companies'
  const [allCompanies, setAllCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);

  const RESULTS_TUTORIAL = "results_tutorial"; //localStorage key for the results page tutorial status
  const driverRef = useRef(
    driver({
      showProgress: true,
      disableActiveInteraction: true,
      overlayClickBehavior: () => {},
      smoothScroll: true,
      popoverClass: "rezify-theme",
      onCloseClick: () => {
        localStorage.setItem(RESULTS_TUTORIAL, true);
        driverRef.current.destroy();
      },
      steps: [
        {
          popover: {
            title: "Welcome to Rezify!",
            description: "Let's take a tour to get you started.",
            disableButtons: [],
            nextBtnText: "Start Tutorial",
            prevBtnText: "Skip Tutorial",
            showProgress: false,
            onPrevClick: () => {
              driverRef.current.moveTo(14);
            },
          },
        },
        // Discover tab
        {
          element: "#filter-All",
          popover: {
            title: "Discover",
            description:
              "The discover tab allows you to browse job recommendations based on your skillset.",
          },
          onHighlightStarted: () => {
            document.getElementById("filter-All").click();
          },
        },
        // Favorites tab
        {
          element: "#filter-Favorites",
          popover: {
            title: "Favorites",
            description:
              "The Favorites tab allows you to view your saved jobs.",
            onPrevClick: () => {
              driverRef.current.moveTo(1);
            },
          },
          onHighlightStarted: () => {
            document.getElementById("filter-Favorites").click();
          },
        },
        // Applied-to tab
        {
          element: "#filter-Applied_to",
          popover: {
            title: "Applied To",
            description:
              "The Applied To tab allows you to view jobs you have previously applied to.",
          },
          onHighlightStarted: () => {
            document.getElementById("filter-Applied_to").click();
          },
        },
        // Discover tab
        {
          element: "#filter-All",
          popover: {
            title: "Discover",
            description: "Let's take a deeper look at the Discover tab.",
          },
          onHighlightStarted: () => {
            document.getElementById("filter-All").click();
          },
        },
        {
          element: "#filter-buttons",
          popover: {
            title: "Position Titles",
            description:
              "This menu allows you to select position titles to include in your recommended job.",
            side: "bottom",
          },
          disableActiveInteraction: false,
        },
        {
          element: "#filter-buttons",
          popover: {
            title: "Selecting Titles",
            description:
              "Clicking a title will toggle whether it is included in your recommendations...",
            side: "bottom",
          },
          disableActiveInteraction: false,
        },
        {
          element: "#filter-buttons",
          popover: {
            title: "Removing Titles",
            description:
              "...and clicking the X will remove the title from your list altogether.",
            side: "bottom",
          },
          disableActiveInteraction: false,
        },
        {
          element: "#filter-buttons",
          popover: {
            title: "Adding Titles",
            description:
              "We have suggested position titles based on your resume. You can also add your own by clicking the plus button.",
            side: "bottom",
          },
          disableActiveInteraction: false,
        },
        {
          element: ".company-search-wrapper",
          popover: {
            title: "Company Search",
            description:
              "You can search for specific companies and see all their available job postings by typing in the company search bar.",
            side: "bottom",
          },
          disableActiveInteraction: false,
          onHighlightStarted: () => {
            document.getElementById("company-search-input")?.focus();
          },
          onDeselected: () => {
            document.body.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
          },
        },
        {
          element: "#filter-button",
          popover: {
            title: "Search Filters",
            description:
              "You can also filter your recommendations by clicking this button.",
            onNextClick: () => {
              document.getElementById("filter-button").click();
              driverRef.current.moveNext();
            },
          },
        },
        {
          element: "#filterBox",
          popover: {
            title: "Search Filters",
            description:
              "You can filter by job type, availability, location, and company size.",
          },
          onDeselected: () => {
            document.getElementById("filter-button").click();
          },
          onCloseClick: () => {
            document.getElementById("filter-button").click();
            driverRef.current.destroy();
          },
        },
        {
          element: ".job-card",
          popover: {
            title: "Jobs Recommendations",
            description:
              "You can browse job recommendations and apply to postings here",
            onPrevClick: () => {
              driverRef.current.moveTo(10);
            },
          },
        },
        {
          element: ".star-checkbox-container",
          popover: {
            title: "Jobs Options",
            description:
              "You can also save jobs to your favorites, mark postings as applied to, and remove jobs from your recommendations.",
          },
        },
        {
          element: ".tour-button",
          popover: {
            title: "Review Tour",
            description:
              "You may review this tour anytime by clicking this button.",
            side: "top",
          },
        },
        {
          popover: {
            title: "You're all set!",
            description:
              "You now have everything you need to get started. Good luck in your internship search!",
            onNextClick: () => {
              localStorage.setItem(RESULTS_TUTORIAL, true);
              driverRef.current.moveNext();
            },
          },
        },
      ],
    })
  );

  useEffect(() => {
    const driverObj = driverRef.current;

    if (
      firstWait &&
      userWait &&
      user.id &&
      !localStorage.getItem(RESULTS_TUTORIAL)
    ) {
      driverObj.drive();
    }

    return () => {
      driverObj.destroy();
    };
  }, [firstWait, userWait, user.id]);

  const { setTheme } = useTheme();

  // Load favorite companies from user filters when user changes
  useEffect(() => {
    if (user?.favorite_companies) {
      setFavoriteCompanyIds(user.favorite_companies);
    }
  }, [user?.favorite_companies]);

  useEffect(() => {
      if (!Array.isArray(allCompanies) || !Array.isArray(favoriteCompanyIds)) {
        setFavoriteCompanyInfo([]);
        return;
      }

      const favoriteIdsSet = new Set(favoriteCompanyIds);

      const filteredFavoriteCompanies = allCompanies.filter(
        (company) => favoriteIdsSet.has(company.id)
      );

      setFavoriteCompanyInfo(filteredFavoriteCompanies);
    }, [allCompanies, favoriteCompanyIds]);

  const [companyJobsLoading, setCompanyJobsLoading] = useState(false);

  const clearCompanyFilters = () => {
      setSelectedCompanyIds([]);
    };

    // Function to fetch jobs from one of the user's favorite companies, used for the "Companies" subtab under "Favorites"
  const fetchFavoriteCompanyJobs = async (company_id) => {

      try {
        setCompanyJobsLoading(true);


        // If no company is selected, clear the favorite company jobs list and return
        if (!company_id || company_id.length === 0) {
            setFavoriteCompanyJobs([]);
            return;
        }

        const url = `/api/get_favorite_company_jobs?companyId=${encodeURIComponent(company_id)}`;

        const resp = await fetch(url, { method: "GET" });
        const data = await resp.json();

        if (data.error_code === 'session_expired') {
            window.location.href = '/';
            return;
        }

        if (!resp.ok) {
            throw new Error(data.error_message || "Failed to load company jobs");
        }

        setFavoriteCompanyJobs(Array.isArray(data.jobs) ? data.jobs : []);

      } catch (error) {
          setFavoriteCompanyJobs([]);
          setErrorMessage("Failed to load jobs for selected company");
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 4000);
          Sentry.captureException(error);
      } finally {
          setCompanyJobsLoading(false);
      }
  };

  // Function that handles when companies are selected
  const loadCompanyJobs = async (companyIds = []) => {
      try {
        setCompanyJobsLoading(true);
        setFiltersDisabled(true);

        const companyIdsString = companyIds.join(",");

        const url =
          companyIdsString.length > 0
            ? `/api/select_companies?companies=${encodeURIComponent(companyIdsString)}`
            : `/api/select_companies?companies=`; // backend should treat empty as clear

        const resp = await fetch(url);

        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          if (data.error_code === 'session_expired') {
            window.location.href = '/';
            return;
          }
          setErrorMessage(data.error_message || "Failed to update company filter");
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 4000);
          return;
        }

        const data = await resp.json();

        // Get the data from the api response and update the state of the frontend accordingly

        if (data.jobs) setJobs(data.jobs);

        if (data.total_jobs) setTotalJobs(data.total_jobs);

        if (data.distinct_titles) setDistinctTitles(data.distinct_titles);

        if (data.get_jobs_error) {
            setErrorMessage("Error Getting Jobs. Please Try Again.");
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
        }

        if (data.user) {
            setUser((prev) =>
              prev.filters
                ? {
                    ...data.user,
                    filters: {
                      ...data.user.filters,
                      selected_filter: prev.filters["selected_filter"],
                    },
                  }
                : data.user
            );

            if (data.user.resume_file == null) {
              window.location.href = "/";
              return;
            }
        }

        if (data.error_message) {
            setErrorMessage(data.error_message);
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
        }

        if (data.redirect) {
            window.location.href = data.redirect;
            return;
        }


      } catch (error) {
        setErrorMessage("Failed to load company jobs");
        setShowErrorPopup(true);
        setTimeout(() => setShowErrorPopup(false), 4000);
        Sentry.captureException(error);
      } finally {
        setCompanyJobsLoading(false);
        setFiltersDisabled(false);
      }
    };

  // Toggle favorite company
  const handleToggleFavoriteCompany = async (company_id, alreadyFavorited) => {
        const isFavorited = favoriteCompanyIds.includes(company_id);

        // Add flag to company object sent to backend
        const companyWithStatus = {
            company_id: company_id,
            already_favorite: alreadyFavorited
        };

        // Optimistic update
        if (isFavorited) {
            setFavoriteCompanyIds((prev) =>
                prev.filter(id => id !== company_id)
            );
            const selectedCompanyId = localStorage.getItem("selectedCompanyId");

            if (String(selectedCompanyId) === String(company_id)) {
                setFavoriteCompanyJobs([]); // <-- make sure this state exists
                localStorage.removeItem("selectedCompanyId");
            }
        } else {
            setFavoriteCompanyIds((prev) => [...prev, company_id]);
        }

        try {
            const response = await fetch("/api/toggle_favorite_company", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ company: companyWithStatus }),
            });

            const result = await response.json();

            if (result.error_code === 'session_expired') {
                window.location.href = '/';
                return;
            }

            if (!response.ok) {
                // Revert optimistic update
                if (isFavorited) {
                    setFavoriteCompanyIds((prev) => [...prev, company_id]);
                } else {
                    setFavoriteCompanyIds((prev) => prev.filter(id => id !== company_id));
                }
                setErrorMessage(result.error_message || "Failed to update company favorites");
                setShowErrorPopup(true);
                setTimeout(() => setShowErrorPopup(false), 3000);
                return;
            }

            if (result.status === "success" && result.favorite_companies) {
                setFavoriteCompanyIds(result.favorite_companies);

                setUser((prev) => ({
                    ...prev,
                    filters: {
                        ...prev.filters,
                        favorite_companies: result.favorite_companies,
                    },
                }));
            }
        } catch (error) {
            if (isFavorited) {
                setFavoriteCompanyIds((prev) => [...prev, company_id]);
            } else {
                setFavoriteCompanyIds((prev) =>
                    prev.filter(id => id !== company_id)
                );
            }

            setErrorMessage("Something went wrong updating favorites");
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 3000);
            Sentry.captureException(error);
        }
    };

  const fetchCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);

      const resp = await fetch("/api/get_all_companies");
      const data = await resp.json();

      if (data.error_code === 'session_expired' && user?.id) {
        window.location.href = '/';
        return;
      }

      if (!resp.ok) {
        if (!user?.id) return;
        throw new Error(data.error_message || "Failed to load companies");
      }

      setAllCompanies(Array.isArray(data.companies) ? data.companies : []);
    } catch (e) {
        setAllCompanies([]);
        // Only show error if user is logged in (has id)
        if (user?.id) {
            setErrorMessage("Error loading companies. Please refresh.");
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
        }
        Sentry.captureException(e);
    } finally {
      setCompaniesLoading(false);
    }
  }, [user?.id]);

  const refreshJobs = (newJobs = null, newUser = null, newtotaljobs = 0) => {
    if (newJobs && newUser) {
      setJobs(newJobs);
      //Don't mess with selected filter, update everything else
      setUser((prev) =>
        prev.filters
          ? {
              ...newUser,
              filters: {
                ...newUser.filters,
                selected_filter: prev.filters["selected_filter"],
              },
            }
          : newUser
      );
      setTotalJobs(newtotaljobs);
    } else {
      setRefreshVal((prev) => prev + 1);
    }
  };

  useEffect(() => {
      setFirstWait(false);
      setUserWait(false);

      const fetchResults = async () => {
        try {
          const response = await fetch("/api/results");
          const data = await response.json();

          if (!response.ok) {
            if (data.error_code === 'session_expired') {
              window.location.href = '/';
              return;
            }
            setErrorMessage(data.error_message || "Error loading results. Please try again.");
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
            return;
          }

          if (data.colors) {
            localStorage.setItem("theme-colors", JSON.stringify(data.colors));
            updateColors(data.colors, setTheme);
          }

          if (data.jobs) setJobs(data.jobs);

          if (data.info_message) {
            let msg = null;
            if (data.info_message.change) msg = "Password successfully changed.";
            else if (data.info_message.auth_type_upgraded) msg = `Authentication upgraded to ${data.info_message.auth_type_upgraded}. Please log in via ${data.info_message.auth_type_upgraded} next time!`;
            if (msg) {
              setInfoMessage(msg);
              setShowInfoPopup(true);
              setTimeout(() => setShowInfoPopup(false), 4000);
            }
          }

          if (data.login_refresh) setLoginRefresh(data.login_refresh);
          if (data.total_jobs) setTotalJobs(data.total_jobs);
          if (data.distinct_titles) setDistinctTitles(data.distinct_titles);

          if (data.user) {
            setUser((prev) =>
              prev.filters
                ? {
                    ...data.user,
                    filters: {
                      ...data.user.filters,
                      selected_filter: prev.filters["selected_filter"],
                    },
                  }
                : data.user
            );

            if (data.user.filters.selected_companies) {
                setSelectedCompanyIds(data.user.filters.selected_companies)
            }

            if (data.user.resume_file == null) {
              window.location.href = "/";
              return;
            }
          }

          if (data.error_message) {
            setErrorMessage(data.error_message);
            setShowErrorPopup(true);
            setTimeout(() => setShowErrorPopup(false), 4000);
          }

          if (data.redirect) {
            window.location.href = data.redirect;
            return;
          }

          return data;
        } catch (err) {
          setErrorMessage("Error: " + (err?.message || "Unknown error"));
          setShowErrorPopup(true);
          setTimeout(() => setShowErrorPopup(false), 4000);
          Sentry.captureException(err);
        } finally {
          setFirstWait(true);
          setUserWait(true);
        }
      };

      (async () => {
        const resultsData = await fetchResults();
        if (!resultsData?.login_refresh) {
          fetchCompanies(); // can run independently
        }
    })();
  }, [refreshVal]);

  return (
    <>
      {/*=== Register Popup Overlay ===*/}
      {firstWait && !user.id && (
        <div className="register-popup-overlay">
          <div className="register-popup-container register-context">
            <RegisterEmail source="results" />
          </div>
        </div>
      )}
      {/*=====================*/}
      {firstWait && userWait ? (
        <TourButton driver={driverRef.current}></TourButton>
      ) : (
        <></>
      )}

      {/*=== Premium Popup Overlay ===*/}
      {premiumPopup && (
        <div className="popup-overlay">
          <div className="popup-container">
            <h2>Upgrade to premium to use this feature!</h2>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <a
                href={"/plans?redirect=results"}
                className="upgrade-button"
                style={{ width: 120, margin: 10 }}
              >
                Upgrade
              </a>
              <button
                className="disabled-refresh-button"
                style={{
                  width: 80,
                  margin: 10,
                  backgroundColor: "#555",
                  cursor: "pointer",
                }}
                onClick={() => {
                  setPremiumPopup(false);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/*=====================*/}

      {/*=== Results Header ===*/}
      <header>
        <div className="top-header" id="top-header">
          <IndexHeader user={user} firstWait={userWait} />
        </div>
        <div className="user-filter-header">
          <div className="top-links">
            {(user.plan === "premium" || true) && (
              <div className="filter-buttons-inline">
                <button
                  id="filter-All"
                  onClick={() => {
                    setUser((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        selected_filter: "All",
                      },
                    }));
                  }}
                  type="button"
                  className={
                    "filter-option " +
                    (user.filters && user.filters["selected_filter"] === "All"
                      ? "selected"
                      : "")
                  }
                >
                  Discover
                </button>
                <button
                  id="filter-Favorites"
                  onClick={() => {
                    setUser((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        selected_filter: "Favorites",
                      },
                    }));
                  }}
                  type="button"
                  className={
                    "filter-option " +
                    (user.filters &&
                    user.filters["selected_filter"] === "Favorites"
                      ? "selected"
                      : "")
                  }
                >
                  Favorites
                </button>
                <button
                  id="filter-Applied_to"
                  onClick={() => {
                    setUser((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        selected_filter: "Applied_to",
                      },
                    }));
                  }}
                  type="button"
                  className={
                    "filter-option " +
                    (user.filters &&
                    user.filters["selected_filter"] === "Applied_to"
                      ? "selected"
                      : "")
                  }
                >
                  Applied To
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      {/*=====================*/}

      {showInfoPopup && (
        <div id="success-popup" className="success-popup">
          {infoMessage}
        </div>
      )}

      {showErrorPopup && <div className="error-popup">{error_message}</div>}

      <div id="container-loading-animation" style={{ display: "none" }}>
        <div className="container-loading-spinner"></div>
      </div>

      {/* Intership / Entry Level Toggle
        {(user.filters && user.filters['selected_filter'] === 'All') && (
            <>
                <div className="top-type-container">
                    <h3>Looking For: </h3>
                    <div className="top-type-filter">
                        <div className="top-links">
                                <button id="filter-internships" onClick={() => {
                                    setSelectedJobType('internships')
                                }} type="button"
                                        className={"filter-option " + ((selectedJobType === 'internships') ? "selected" : "")}>
                                    Internships / Co-ops
                                </button>
                                <button id="filter-entry-level" onClick={() => {
                                    setSelectedJobType('entry_level')
                                }}
                                type="button"
                                        className={"filter-option " + ((selectedJobType === 'entry_level') ? "selected" : "")
                                        }>
                                    Entry Level Positions
                                </button>
                        </div>
                    </div>
            </div>
            </>
        )}
        */}

      <div className="container">
        {selectedJobType === "internships" && (
          <>
            <div id="filter-section" className="filter-section">
              <FiltersSection
                user={user}
                distinct_titles={distinct_titles}
                jobs={Array.isArray(jobs) ? jobs : []}
                setUser={setUser}
                refreshJobs={refreshJobs}
                setFirstWait={setFirstWait}
                firstWait={firstWait}
                setErrorMessage={setErrorMessage}
                setShowErrorPopup={setShowErrorPopup}
                selectedJobType={selectedJobType}
                filtersDisabled={filtersDisabled}
                setFiltersDisabled={setFiltersDisabled}
                selectedCompanyIds={selectedCompanyIds}
                setSelectedCompanyIds={setSelectedCompanyIds}
                favoriteCompanyIds={favoriteCompanyIds}
                favoriteCompanyInfo={favoriteCompanyInfo}
                onToggleFavoriteCompany={handleToggleFavoriteCompany}
                favoritesSubTab={favoritesSubTab}
                setFavoritesSubTab={setFavoritesSubTab}
                onLoadCompanyJobs={loadCompanyJobs}
                onLoadFavoriteCompanyJobs={fetchFavoriteCompanyJobs}
                allCompanies={allCompanies}
                companiesLoading={companiesLoading}
                onRefreshCompanies={fetchCompanies}
              />
            </div>

            {!firstWait && (
              <div id="job-cards-container" className="job-cards-container">
                <div id="loading-animation">
                  <div className="loading-spinner"></div>
                </div>
              </div>
            )}

            <div
              id="refresh-animation"
              style={{ display: "none", textAlign: "center" }}
            >
              <div className="loading-spinner" style={{ margin: 0 }}></div>
              <p
                style={{
                  color: "var(--primary-color)",
                  fontWeight: "bold",
                  marginTop: "10px",
                }}
              >
                Getting up-to-date results...
              </p>
            </div>
            {firstWait && !companyJobsLoading && (
              <div id="job-cards-container" className="job-cards-container">
                {user.filters ? (
                  /* NORMAL USER :) */
                  <JobTitles
                    user={user}
                    jobs={
                      user.filters["selected_filter"] === "All"
                        ? jobs
                        : user.filters["selected_filter"] === "Favorites"
                        ? favoritesSubTab === "jobs"
                          ? user.favorites
                          : // For companies tab, show jobs from favorite companies
                            favoriteCompanyJobs
                        : user.applied_to
                    }
                    total_jobs={
                      user.filters["selected_filter"] === "All"
                        ? total_jobs
                        : user.filters["selected_filter"] === "Favorites"
                        ? favoritesSubTab === "jobs"
                          ? user.favorites
                            ? user.favorites.length
                            : 0
                          : favoriteCompanyJobs.length
                        : user.applied_to
                        ? user.applied_to.length
                        : 0
                    }
                    loginRefresh={loginRefresh}
                    setLoginRefresh={setLoginRefresh}
                    finding={user.filters["selected_filter"] === "All"}
                    setUser={setUser}
                    setJobs={setJobs}
                    setTotalJobs={setTotalJobs}
                    refreshJobs={refreshJobs}
                    setFirstWait={setFirstWait}
                    setPremiumPopup={setPremiumPopup}
                    setErrorMessage={setErrorMessage}
                    setShowErrorPopup={setShowErrorPopup}
                    selectedJobType={selectedJobType}
                    filtersDisabled={filtersDisabled}
                    setFiltersDisabled={setFiltersDisabled}
                    favoriteCompanyIds={favoriteCompanyIds}
                    onToggleFavoriteCompany={handleToggleFavoriteCompany}
                    clearCompanyFilters={clearCompanyFilters}
                    selectedCompanyIds={selectedCompanyIds}
                    onRefreshCompanies={fetchCompanies}
                    favoritesSubTab={favoritesSubTab}
                  />
                ) : (
                  /* UNINITIALIZED USER !!!! */
                  <JobTitles
                    user={user}
                    jobs={jobs}
                    total_jobs={total_jobs}
                    finding={true}
                    setUser={() => {}}
                    setJobs={() => {}}
                    refreshJobs={() => {}}
                    setFirstWait={setFirstWait}
                    setPremiumPopup={setPremiumPopup}
                    selectedJobType={selectedJobType}
                    filtersDisabled={filtersDisabled}
                    setFiltersDisabled={setFiltersDisabled}
                  />
                )}
              </div>
            )}
            {companyJobsLoading && (
              <div id="job-cards-container" className="job-cards-container">
                <div id="loading-animation">
                  <div className="loading-spinner"></div>
                </div>
              </div>
            )}
          </>
        )}
        {selectedJobType === "entry_level" && (
          <>
            <section className="coming-soon">
              <img
                className="cs-logo"
                src={"/static/rezify_logo2.png"}
                alt="Rezify logo"
              />

              <h1 className="cs-title">Coming Soon…</h1>
              <p className="cs-sub">
                Rezify will feature all entry level positions for new graduates
                in the near future!
              </p>
              <p className="cs-sub">Follow our Instagram to stay tuned</p>

              <a
                className="cs-cta"
                href="https://www.instagram.com/rezify.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Follow @rezify.ai
              </a>

              <div className="insta-embed">
                <blockquote
                  className="instagram-media"
                  data-instgrm-permalink="https://www.instagram.com/rezify.ai/?utm_source=ig_embed&utm_campaign=loading"
                  data-instgrm-version="14"
                  style={{
                    background: "#FFF",
                    border: 0,
                    borderRadius: 12,
                    boxShadow:
                      "0 0 1px 0 rgba(0,0,0,.5), 0 8px 24px 0 rgba(0,0,0,.15)",
                    margin: "8px auto",
                    maxWidth: 540,
                    minWidth: 326,
                    width: "100%",
                  }}
                >
                  {/* Instagram will replace this blockquote */}
                </blockquote>
              </div>
            </section>
          </>
        )}

        {user.id && (
          <a href="/index?noredir=1" className="back-button">
            Back to Search
          </a>
        )}
      </div>
    </>
  );
}

export { Results };